import clsx from 'clsx'
import CommentHubAvatar from '../comments/CommentHubAvatar'
import { AuthContext } from '../contexts/AuthContext'
import styles from './AccountAvatar.module.css'

/**
 * The reader's own disc: their commentHub photo (Google's, synced on sign-in) or, without one or
 * when it fails to load, their initials. The generic part of SoundGuys' `LetterAvatar` — SoundGuys
 * keeps its hub letter fill and tier ring on top of its own. The photo comes from the reader's
 * session (`AuthContext`, absent outside the provider and during SSR), so this is for the
 * reader's own surfaces only, never shared-cached HTML.
 */
export const AccountAvatar: React.VFC<{
  /** The name the initials come from; empty = a quiet empty disc (session still loading). */
  name: string
  /** Drawn size in px: 32 the header, 44 the avatar menu's head, 56 a card, 72 the account header. */
  size?: 32 | 36 | 44 | 56 | 72
  /** A photo to show instead of the session's (a preview of someone else's). */
  src?: string | null
  /** The initials' fill: `neutral` grey (account cards), `accent` the brand's p-60 with dark ink
   *  in both themes (Android Authority's header disc — its green takes dark text). */
  tone?: 'neutral' | 'accent'
  className?: string
}> = ({ name, size = 32, src, tone = 'neutral', className }) => {
  const session = AuthContext.useOptionalContainer()?.authState?.user?.avatarUrl ?? null
  const photo = src === undefined ? session : src
  return (
    <span
      className={clsx(
        styles.disc,
        styles[`s${size}`],
        tone === 'accent' && styles.accent,
        !name && !photo && styles.empty,
        className
      )}
      aria-hidden="true"
    >
      {(name || photo) && (
        <CommentHubAvatar
          key={photo ?? ''}
          src={photo}
          name={name}
          initials={2}
          lazy={false}
          fallback="text"
        />
      )}
    </span>
  )
}

export default AccountAvatar
